import { FilterInput, PaginationData, QueryInput } from "./entity-instance.service";
import { createGraphQLClient } from "./graphql.service";
import { gql } from "graphql-request";
import { paginationDataStr } from "@repo/utils/utils";

export interface PyloUserListItem {
  id: string;
  email: string;
  created_at?: string;
  last_login_at?: string | null;
  user_profile?: {
    data?: {
      firstname?: string;
      lastname?: string;
    };
  };
  pylo_app?: {
    data?: {
      id: string;
      name: string;
    };
  };
  pylo_user_invites?: {
    data?: Array<{
      email: string;
      valid_until: string;
    }> | null;
  } | null;
}

interface PyloUserListResponse {
  pyloUserList: {
    data: PyloUserListItem[];
    pagination: PaginationData;
  };
}

export const getPyloUserList = async ({
  query = [],
  cursor = 1,
  per_page = 20,
}: {
  query?: QueryInput[];
  cursor?: number;
  per_page?: number;
}) => {
  const client = createGraphQLClient();
  const filter: FilterInput = {
    query: [
      {
        and: [
          {
            condition: {
              field: "pylo_api_key.id",
              operator: "isNull",
            },
          },
          ...query,
        ],
      },
    ],
  };
  const doc = gql`
  query pyloUserList($filter: FilterInput, $cursor: Int, $per_page: Int) {
    pyloUserList(pagination: {per_page: $per_page, page: $cursor}, filter: $filter) {
        data {
            id,
            email,
            created_at,
            last_login_at,
            user_profile {
                data {
                    firstname
                    lastname
                }
            }
            pylo_app {
                data {
                    id
                    name
                }
            }
            pylo_user_invites {
                data {
                    email
                    valid_until
                }
            }
        }
        ${paginationDataStr}
    } 
  }`;
  const response = await client.request<PyloUserListResponse>(doc, {
    filter,
    cursor,
    per_page,
  });
  return response.pyloUserList;
};

interface InviteUserResponse {
  inviteUser: {
    id: string;
    email: string;
    valid_until: string;
    created_at: string;
    updated_at: string;
  };
}

export const invitePyloUser = async ({
  email,
  pyloAppId,
  createUserDirectly = true,
}: {
  email: string;
  pyloAppId: string;
  createUserDirectly: boolean;
}) => {
  const client = createGraphQLClient();
  const doc = gql`
    mutation inviteUser($input: PyloInviteUserInput!) {
      inviteUser(input: $input) {
        id
        email
        valid_until
        created_at
        updated_at
      }
    }
  `;

  const response = await client.request<InviteUserResponse>(doc, {
    input: {
      email,
      pylo_app_id: pyloAppId,
      create_user_directly: createUserDirectly,
    },
  });

  return response.inviteUser;
};

export const deletePyloUsers = async (ids: string[]) => {
  const client = createGraphQLClient();
  const doc = gql`
    mutation deletePyloUser($ids: [ID!]!) {
      deletePyloUser(ids: $ids) {
        data {
          success
        }
      }
    }
  `;
  await client.request(doc, { ids });
  return true;
};

export interface PyloUserInvitePublicData {
  id: string;
  email: string;
  valid_until: string;
  customer_id: string;
  customer_name: string;
  app_id: string;
  app_name: string;
}

interface InviteByCodePublicResponse {
  inviteByCodePublic: {
    data: PyloUserInvitePublicData;
  };
}

export const getInviteByCode = async (code: string): Promise<PyloUserInvitePublicData> => {
  const client = createGraphQLClient({ dontSendAuthToken: true });
  const doc = gql`
    query inviteByCodePublic($code: String!) {
      inviteByCodePublic(code: $code) {
        data {
          id
          email
          valid_until
          customer_id
          customer_name
          app_id
          app_name
        }
      }
    }
  `;
  const response = await client.request<InviteByCodePublicResponse>(doc, { code });
  return response.inviteByCodePublic.data;
};

export interface PyloUserInviteItem {
  id: string;
  email: string;
  valid_until: string;
  created_at: string;
  updated_at: string;
}

interface PyloUserInviteListResponse {
  entityList: {
    data: PyloUserInviteItem[];
    pagination: PaginationData;
  };
}

export const getPyloUserInvites = async ({
  emails,
}: {
  emails: string[];
}): Promise<PyloUserInviteItem[]> => {
  if (emails.length === 0) return [];

  const client = createGraphQLClient();
  const filter: FilterInput = {
    query: [
      {
        or: emails.map((email) => ({
          condition: {
            field: "email",
            operator: "equal",
            value: email,
          },
        })),
      },
    ],
  };

  const doc = gql`
    query pyloUserInviteList($filter: FilterInput, $cursor: Int, $per_page: Int) {
      entityList(entityName: "PyloUserInvite", pagination: {per_page: $per_page, page: $cursor}, filter: $filter) {
        data {
          id
          email
          valid_until
          created_at
          updated_at
        }
        ${paginationDataStr}
      } 
    }
  `;

  const response = await client.request<PyloUserInviteListResponse>(doc, {
    filter,
    cursor: 1,
    per_page: 1000, // Get all invites at once since we're filtering by specific emails
  });

  return response.entityList.data || [];
};
